import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, SlidersHorizontal } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import PhotoLightboxWithInfo from '../components/PhotoLightboxWithInfo';
import ScrollControls from '../components/ScrollControls';
import SearchableSelect from '../components/SearchableSelect';
import { YearFilter } from '../components/YearFields';
import { validateYearFilters } from '../utils/dating';
import ThumbnailImage from '../components/ThumbnailImage';
import { useLanguage } from '../hooks/useLanguage';
import {
  localiseCategoryName,
  localiseDetailSubtypeName,
  localiseDetailTypeName,
  localiseManufacturerName,
} from '../i18n/filterTranslations';

const PAGE_SIZES = [20, 40, 60, 100, 250];

const copy = {
  RU: { kicker: 'ФОТОАРХИВ', title: 'Галерея', intro: 'Фотографии предметов, маркировок и деталей без отрыва от каталожных данных.', settings: 'Настройки галереи', close: 'Закрыть настройки', year: 'Год', maker: 'Производитель', category: 'Категория', type: 'Тип детали', subtype: 'Разновидность', any: 'Любое значение', size: 'Фотографий на странице', reset: 'Сбросить', empty: 'Фотографий по этим условиям не найдено.', loading: 'Загрузка фотографий…', error: 'Не удалось загрузить галерею.', photo: 'Фото', previous: 'Назад', next: 'Вперёд', selected: 'Выбрано' },
  EN: { kicker: 'PHOTO ARCHIVE', title: 'Gallery', intro: 'Photographs of objects, markings and parts, linked to their catalogue records.', settings: 'Gallery settings', close: 'Close settings', year: 'Year', maker: 'Maker', category: 'Category', type: 'Part type', subtype: 'Variant', any: 'Any value', size: 'Photos per page', reset: 'Reset', empty: 'No photographs match these settings.', loading: 'Loading photographs…', error: 'Unable to load the gallery.', photo: 'Photo', previous: 'Previous', next: 'Next', selected: 'Selected' },
};

function getFilters(params) {
  return Object.fromEntries([...params.entries()].filter(([key]) => !['page', 'size'].includes(key)));
}

function pageNumbers(current, total) {
  const values = new Set([0, total - 1, current - 1, current, current + 1]);
  return [...values].filter((value) => value >= 0 && value < total).sort((a, b) => a - b);
}

function GallerySkeleton({ count }) {
  return (
    <div className="contact-grid" aria-hidden="true">
      {Array.from({ length: count }, (_, index) => (
        <div className="contact-print contact-print--skeleton" key={index}>
          <span className="contact-print__image" />
          <span className="contact-print__caption"><small>&nbsp;</small><strong>&nbsp;</strong></span>
        </div>
      ))}
    </div>
  );
}

export default function GalleryPage() {
  const { lang } = useLanguage();
  const labels = copy[lang] || copy.RU;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const page = Math.max(0, Number.parseInt(searchParams.get('page') || '0', 10) || 0);
  const parsedSize = Number(searchParams.get('size'));
  const pageSize = PAGE_SIZES.includes(parsedSize) ? parsedSize : 40;

  const [photos, setPhotos] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [status, setStatus] = useState('loading');
  const [manufacturers, setManufacturers] = useState([]);
  const [types, setTypes] = useState([]);
  const [settingsOpen, setSettingsOpen] = useState(() => Object.keys(getFilters(searchParams)).length > 0);
  const [lightboxIndex, setLightboxIndex] = useState(-1);

  const filters = useMemo(() => getFilters(searchParams), [searchParams]);
  const [draftFilters, setDraftFilters] = useState(filters);
  const [draftSize, setDraftSize] = useState(pageSize);
  const [filterError, setFilterError] = useState('');
  const [resetRevision, setResetRevision] = useState(0);
  useEffect(() => { setDraftFilters(filters); setDraftSize(pageSize); setFilterError(''); }, [filters, pageSize]);
  const uploadHost = process.env.NODE_ENV === 'development' ? 'http://localhost:8080' : '';

  useEffect(() => {
    Promise.all([api.get('/manufacturers'), api.get('/detailTypes')])
      .then(([makers, detailTypes]) => {
        setManufacturers(Array.isArray(makers.data) ? makers.data : []);
        setTypes(Array.isArray(detailTypes.data) ? detailTypes.data : []);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    let active = true;
    setStatus('loading');
    api.get('/photos/page', { params: { ...filters, page, size: pageSize } })
      .then(({ data }) => {
        if (!active) return;
        setPhotos(Array.isArray(data?.content) ? data.content : []);
        setTotalPages(Number.isFinite(data?.totalPages) ? data.totalPages : 1);
        setTotalElements(Number.isFinite(data?.totalElements) ? data.totalElements : (data?.content?.length || 0));
        setStatus('ready');
      })
      .catch(() => active && setStatus('error'));
    return () => { active = false; };
  }, [filters, page, pageSize]);

  const makerOptions = useMemo(() => manufacturers.map((maker) => ({
    id: maker.name,
    name: localiseManufacturerName(maker, lang),
  })), [lang, manufacturers]);
  const categoryOptions = useMemo(() => [...new Set(types.map((type) => type.belongsTo).filter(Boolean))].map((name) => ({
    id: name,
    name: localiseCategoryName(name, lang),
  })), [lang, types]);
  const availableTypes = useMemo(() => types.filter((type) => !draftFilters.belongsTo || type.belongsTo === draftFilters.belongsTo), [draftFilters.belongsTo, types]);
  const typeOptions = useMemo(() => availableTypes.map((type) => ({
    id: type.name,
    name: localiseDetailTypeName(type, lang),
  })), [availableTypes, lang]);
  const selectedType = types.find((type) => type.name === draftFilters.type);
  const subtypeOptions = useMemo(() => (selectedType?.subtypes || []).map((subtype) => ({
    id: subtype.name,
    name: localiseDetailSubtypeName(subtype, lang),
  })), [lang, selectedType]);

  const navigateWith = (nextFilters = filters, nextPage = 0, nextSize = pageSize) => {
    const params = new URLSearchParams({ page: String(nextPage), size: String(nextSize) });
    Object.entries(nextFilters).forEach(([key, value]) => value && params.set(key, value));
    navigate('/gallery?' + params.toString());
  };

  const setFilter = (key, value) => {
    const next = { ...draftFilters, [key]: value || '' };
    if (key === 'belongsTo') {
      const validTypes = types.filter((type) => !value || type.belongsTo === value).map((type) => type.name);
      if (!validTypes.includes(next.type)) { next.type = ''; next.subtype = ''; }
    }
    if (key === 'type') next.subtype = '';
    setDraftFilters(next);
  };

  const visiblePages = pageNumbers(page, totalPages);

  return (
    <main className="gallery-page page-width">
      <ScrollControls />
      <header className="catalog-header gallery-header">
        <p className="section-kicker">{labels.kicker}</p>
        <div><h1>{labels.title}</h1><strong>{status === 'ready' ? totalElements : '—'}</strong></div>
        <p>{labels.intro}</p>
      </header>

      <div className="gallery-toolbar">
        <button type="button" className={settingsOpen ? 'catalog-settings-button is-active' : 'catalog-settings-button'} onClick={() => setSettingsOpen((value) => !value)}>
          <SlidersHorizontal size={17} />{settingsOpen ? labels.close : labels.settings}
          {Object.values(filters).filter(Boolean).length > 0 && <span>{Object.values(filters).filter(Boolean).length}</span>}
        </button>
      </div>

      {settingsOpen && (
        <section className="catalog-settings gallery-settings" aria-label={labels.settings}>
          <div className="catalog-settings__grid">
            <YearFilter key={searchParams.toString() + resetRevision} filters={draftFilters} initialFilters={filters} onChange={setDraftFilters} />
            <label><span>{labels.maker}</span><SearchableSelect options={makerOptions} value={draftFilters.manufacturer || null} onChange={(value) => setFilter('manufacturer', value)} placeholder={labels.any} /></label>
            <label><span>{labels.category}</span><SearchableSelect options={categoryOptions} value={draftFilters.belongsTo || null} onChange={(value) => setFilter('belongsTo', value)} placeholder={labels.any} /></label>
            <label><span>{labels.type}</span><SearchableSelect options={typeOptions} value={draftFilters.type || null} onChange={(value) => setFilter('type', value)} placeholder={labels.any} /></label>
            {subtypeOptions.length > 0 && <label><span>{labels.subtype}</span><SearchableSelect options={subtypeOptions} value={draftFilters.subtype || null} onChange={(value) => setFilter('subtype', value)} placeholder={labels.any} /></label>}
            <label><span>{labels.size}</span><select value={draftSize} onChange={(event) => setDraftSize(Number(event.target.value))}>{PAGE_SIZES.map((size) => <option value={size} key={size}>{size}</option>)}</select></label>
          </div>
          {filterError && <p role="alert">{filterError}</p>}
          <div className="catalog-settings__actions"><button type="button" className="primary-button" onClick={() => {
            try { validateYearFilters(draftFilters, lang); setFilterError(''); navigateWith(draftFilters, 0, draftSize); }
            catch (error) { setFilterError(error.message); }
          }}>{lang === 'EN' ? 'Apply' : 'Применить'}</button><button type="button" className="text-button" onClick={() => { setDraftFilters({}); setFilterError(''); setResetRevision(value => value + 1); navigateWith({}); }}>{labels.reset}</button></div>
        </section>
      )}

      <section className="gallery-content" aria-label={labels.title} aria-live="polite" aria-busy={status === 'loading'}>
        {status === 'loading' && <>
          <p className="sr-only" role="status">{labels.loading}</p>
          <GallerySkeleton count={pageSize} />
        </>}
        {status === 'error' && <p className="archive-notice archive-notice--error">{labels.error}</p>}
        {status === 'ready' && photos.length === 0 && <p className="archive-notice">{labels.empty}</p>}
        {status === 'ready' && photos.length > 0 && (
          <div className="contact-grid">
            {photos.map((photo, index) => {
              const source = photo.thumbnailUrl || photo.url;
              const src = source?.startsWith('/') ? uploadHost + source : source;
              return (
                <button type="button" className="contact-print" key={photo.id} onClick={() => setLightboxIndex(index)}>
                  <span className="contact-print__image"><ThumbnailImage src={src} alt={`${labels.photo} ${photo.id}`} /></span>
                  <span className="contact-print__caption"><small>{labels.photo}</small><strong>№ {photo.id}</strong></span>
                </button>
              );
            })}
          </div>
        )}

        {status === 'ready' && totalPages > 1 && (
          <nav className="catalog-pagination gallery-pagination" aria-label="Pagination">
            <button type="button" disabled={page === 0} onClick={() => navigateWith(filters, page - 1)}><ChevronLeft size={16} />{labels.previous}</button>
            <div>{visiblePages.map((value, index) => <React.Fragment key={value}>{index > 0 && value - visiblePages[index - 1] > 1 && <span>…</span>}<button type="button" className={value === page ? 'is-active' : ''} onClick={() => navigateWith(filters, value)}>{value + 1}</button></React.Fragment>)}</div>
            <button type="button" disabled={page >= totalPages - 1} onClick={() => navigateWith(filters, page + 1)}>{labels.next}<ChevronRight size={16} /></button>
          </nav>
        )}
      </section>

      <PhotoLightboxWithInfo photos={photos} open={lightboxIndex >= 0} index={lightboxIndex} onClose={() => setLightboxIndex(-1)} onIndexChange={setLightboxIndex} />
    </main>
  );
}
